import { useEffect, useState } from "react";
import {
  Typography,
  Button,
  AppBar,
  IconButton,
  Toolbar,
  DialogContent,
  Stack,
  Box,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonRemove as DissociateIcon,
} from "@mui/icons-material";
import { prettify, toastError } from "../../../utils/utils";
import { useAuthUser } from "react-auth-kit";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";
import { EditableInputDisplay, useEditMode } from "../../EditableComponents";
import { useDeleteKit, useGetKitById, usePutKitById } from "../useKitQueries";
import KitAssociationFields from "../KitAssociationFields/KitAssociationFields";
import KitDevicesTable from "../KitDevicesTable/KitDevicesTable";
import ConfirmDialog from "../../ConfirmDialog/ConfirmDialog";

function ViewKit({ kit_id, closeModal, _editMode }) {
  const authUser = useAuthUser();
  const isAssignOnly =
    authUser()?.role === 2 || authUser()?.role === 3;
  const canEditOrg = authUser()?.role === 1;

  const queryClient = useQueryClient();
  const kitQuery = useGetKitById(kit_id);
  const editMutation = usePutKitById(kit_id);
  const deleteKitMutation = useDeleteKit();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    editMode,
    enterEditMode,
    confirmEditMode,
    updateEditState,
    updateEditStateMany,
  } = useEditMode({
    editModeEnabled: _editMode || isAssignOnly,
    updateMutation: editMutation,
    initialQueryKey: ["kits", kit_id],
  });

  useEffect(() => {
    if (_editMode || isAssignOnly) enterEditMode(true);
  }, [_editMode, isAssignOnly]);

  const handleConfirm = () => {
    if (canEditOrg && !isAssignOnly) {
      const current =
        queryClient.getQueryData(["kits", kit_id]) ?? kitQuery.data;
      if (current?.org_id == null || current?.org_id === "") {
        toast.error("Organizzazione è un campo obbligatorio", toastError);
        return;
      }
    }
    confirmEditMode();
  };

  const handleDissociatePatient = () => {
    if (!kitQuery.data) return;
    editMutation.mutate({
      ...kitQuery.data,
      patient_id: null,
    });
  };

  const handleDeleteKit = () => {
    deleteKitMutation.mutate(kit_id, {
      onSuccess: () => {
        setConfirmDelete(false);
        closeModal();
      },
    });
  };

  const kit =
    queryClient.getQueryData(["kits", kit_id]) ?? kitQuery.data;
  const associationEditMode = isAssignOnly || editMode;

  return (
    <>
      <ConfirmDialog
        open={confirmDelete}
        title="Elimina kit"
        message={`Vuoi eliminare definitivamente il kit "${kitQuery.data?.name ?? ""}"?`}
        confirmLabel="Elimina"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDeleteKit}
      />

      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={closeModal}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      {kitQuery.isLoading ? (
        <LoadingPaper />
      ) : kitQuery.isError ? (
        <LoadingError error={kitQuery.error} />
      ) : (
        <DialogContent>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h5" component="div">
            {isAssignOnly
              ? `Gestisci kit: ${prettify(kitQuery.data.name)}`
              : `Kit: ${prettify(kitQuery.data.name)}`}
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Button
                style={{ margin: "5px" }}
                color={editMode ? "success" : "warning"}
                variant="contained"
                onClick={editMode ? handleConfirm : () => enterEditMode(true)}
              >
                <EditIcon sx={{ marginRight: "5px" }} />
                {editMode ? "Conferma" : "Modifica"}
              </Button>
              <Button
                style={{ margin: "5px" }}
                variant="contained"
                disabled={kitQuery.data.patient_id == null}
                onClick={handleDissociatePatient}
              >
                <DissociateIcon sx={{ marginRight: "5px" }} />
                Dissocia paziente
              </Button>
              {canEditOrg && (
                <Button
                  style={{ margin: "5px" }}
                  color="error"
                  variant="contained"
                  onClick={() => setConfirmDelete(true)}
                >
                  <DeleteIcon sx={{ marginRight: "5px" }} />
                  Elimina kit
                </Button>
              )}
            </Box>

            <Box sx={{ margin: "10px", maxWidth: 480 }}>
              {!isAssignOnly && (
                <EditableInputDisplay
                  id="name"
                  label="Nome:"
                  initialValue={kitQuery.data.name}
                  setEditMode={editMode}
                  inputMode="text"
                  setParentValue={updateEditState}
                />
              )}

              <KitAssociationFields
                orgId={kit?.org_id}
                patientId={kit?.patient_id}
                editMode={associationEditMode}
                canEditOrg={canEditOrg}
                onFieldsChange={updateEditStateMany}
              />
            </Box>

            <Box sx={{ margin: "10px", width: "95%" }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                <strong>Strumenti</strong>
              </Typography>
              <KitDevicesTable
                kitId={kit_id}
                orgId={kit?.org_id ?? kitQuery.data?.org_id}
                devices={kit?.devices ?? kitQuery.data?.devices ?? []}
              />
            </Box>
          </Stack>
        </DialogContent>
      )}
    </>
  );
}

export default ViewKit;
